import { createAuthWidget } from "./AuthWidget";
import { createBackToLandingLink } from "./BackToLandingLink";
import { isDiscordActivity } from "@bn-games/discord-activity";

export interface MenuCornersHandle {
  destroy(): void;
}

const NOOP_HANDLE: MenuCornersHandle = { destroy(): void {} };

// The Capacitor global is only injected by a native iOS/Android shell, so its
// presence is our proxy for "running inside a WKWebView/system WebView" vs.
// the regular browser PWA — same zero-dependency shape as isDiscordActivity.
// Duplicated from apps/sudoku/src/native/platform.ts rather than imported:
// that file is app-local (single Capacitor adopter, no shared package yet
// per root CLAUDE.md's extraction trigger), and this check has no other
// dependency worth wiring up just to reuse five lines.
function isCapacitorNative(): boolean {
  return (
    typeof window !== "undefined" &&
    Boolean((window as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor?.isNativePlatform?.())
  );
}

/**
 * Mounts the back-to-landing link (top-left) and the auth widget (top-right)
 * into a main-menu root, in that order so keyboard tab order follows the
 * left-to-right visual layout.
 *
 * Auto-hides both widgets inside a Discord Activity iframe or a Capacitor
 * native shell: Discord Activities have their own SDK-based identity, and
 * Capacitor's WKWebView/system WebView breaks the OAuth popup — in both
 * cases an external link out of the embed also risks breaking it (Capacitor:
 * App Store Guideline 4.2). Every caller gets this for free; no per-app flag
 * to remember.
 */
export function mountMenuCorners(root: HTMLElement): MenuCornersHandle {
  if (isDiscordActivity() || isCapacitorNative()) {
    return NOOP_HANDLE;
  }

  const homeLink = createBackToLandingLink();
  homeLink.root.classList.add("menu-corner--top-left");
  root.appendChild(homeLink.root);

  const authWidget = createAuthWidget();
  authWidget.root.classList.add("menu-corner--top-right");
  root.appendChild(authWidget.root);

  return {
    destroy(): void {
      authWidget.destroy();
      authWidget.root.remove();
      homeLink.destroy();
      homeLink.root.remove();
    },
  };
}
